import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllUsers } from '../services/adminService';
import { Search, User, UserCheck, UserX } from 'lucide-react';
import './UsersScreen.css';

export default function UsersScreen() {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'user' | 'familyMember'>('all');

  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: getAllUsers,
  });

  const filteredUsers = users?.filter(user => {
    const matchesSearch = 
      user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  }) || [];

  const stats = {
    total: users?.length || 0,
    admins: users?.filter(u => u.role === 'admin').length || 0,
    caregivers: users?.filter(u => u.role === 'user').length || 0,
    members: users?.filter(u => u.role === 'familyMember').length || 0,
  };

  if (isLoading) {
    return <div className="loading-state">Loading users...</div>;
  }

  return (
    <div className="users-screen">
      <div className="screen-header">
        <div>
          <h1>Users</h1>
          <p>Manage all users across all families</p>
        </div>
      </div>

      <div className="stats-bar">
        <div className="stat-item">
          <User size={20} />
          <div>
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Users</span>
          </div>
        </div>
        <div className="stat-item">
          <UserCheck size={20} />
          <div>
            <span className="stat-value">{stats.caregivers}</span>
            <span className="stat-label">Caregivers</span>
          </div>
        </div>
        <div className="stat-item">
          <UserX size={20} />
          <div>
            <span className="stat-value">{stats.members}</span>
            <span className="stat-label">Members</span>
          </div>
        </div>
        <div className="stat-item">
          <User size={20} />
          <div>
            <span className="stat-value">{stats.admins}</span>
            <span className="stat-label">Admins</span>
          </div>
        </div>
      </div>

      <div className="filters">
        <div className="search-bar">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="role-filters">
          {(['all', 'admin', 'user', 'familyMember'] as const).map((role) => (
            <button
              key={role}
              className={`role-filter ${roleFilter === role ? 'active' : ''}`}
              onClick={() => setRoleFilter(role)}
            >
              {role === 'all' ? 'All' : role.charAt(0).toUpperCase() + role.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <div className="empty-state">No users found.</div>
      ) : (
        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Family</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="user-cell">
                      <div className="user-avatar-small">
                        {user.displayName[0].toUpperCase()}
                      </div>
                      <span>{user.displayName}</span>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge ${user.role}`}>{user.role}</span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.isActive === false ? 'inactive' : 'active'}`}>
                      {user.isActive === false ? 'Inactive' : 'Active'}
                    </span>
                  </td>
                  <td>{user.familyId || 'N/A'}</td>
                  <td>
                    {user.createdAt?.toDate
                      ? new Date(user.createdAt.toDate()).toLocaleDateString()
                      : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

